import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import { useEnterpriseDetail } from "../api/hooks";
import { PROGRESS_STEPS } from "../api/types";
import { formatDate, formatDateTime, stepTone } from "../lib/format";
import { QueryState } from "./QueryState";
import { Pill } from "./ui";

// The detail endpoint types these method fields loosely (plain dicts); narrow
// them to the shapes the serializer actually returns.
interface RelatedProfile {
  id: string;
  name: string;
}
interface DetailActionPlan {
  id: string;
  name: string;
  created_at: string;
}
interface CommunicationStatus {
  status: "none" | "requested" | "published";
  at: string | null;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-0.5 py-1 text-sm sm:grid-cols-3 sm:gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground sm:col-span-2">{children}</dd>
    </div>
  );
}

/** Slide-over showing the full report view for one enterprise.
 *
 * `onViewMember` opens the enterprise's own diagnostic / action plan on its
 * behalf. It is offered only where the enterprise consented — viewing needs
 * `consent_diagnostic_at`, editing needs `consent_edit_at` — which is the same
 * gate the server enforces through `resolve_effective_user`.
 */
export function EnterpriseDetailPanel({
  id,
  onClose,
  onViewMember,
}: {
  id: string;
  onClose: () => void;
  onViewMember?: (profileId: string, name: string, tab: "diagnostic" | "plan") => void;
}) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useEnterpriseDetail(id);
  const none = t("reports.detail.none");

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label={t("reports.detail.close")}
        className="flex-1 bg-black/40"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-label={t("reports.detail.title")}
        className="h-full w-full max-w-md overflow-y-auto bg-background p-6 shadow-xl"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-xl font-bold text-foreground">
            {data?.display_name ?? t("reports.detail.title")}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X aria-hidden />
            <span className="sr-only">{t("reports.detail.close")}</span>
          </Button>
        </div>

        <QueryState isLoading={isLoading} isError={isError}>
          {data ? (
            <div className="space-y-6">
              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                  {t("reports.detail.contact")}
                </h3>
                <dl>
                  <Row label={t("reports.detail.owner")}>
                    {`${data.user_first_name} ${data.user_last_name}`.trim() ||
                      none}
                  </Row>
                  <Row label={t("reports.columns.email")}>
                    {data.user_email || none}
                  </Row>
                  <Row label={t("reports.detail.phone")}>
                    {data.phone_number || none}
                  </Row>
                  <Row label={t("reports.detail.address")}>
                    {[data.address, data.city, data.region, data.province]
                      .filter(Boolean)
                      .join(", ") || none}
                  </Row>
                  <Row label={t("reports.detail.mrc")}>{data.mrc || none}</Row>
                  <Row label={t("reports.columns.type")}>
                    {data.enterprise_type_display || none}
                  </Row>
                  <Row label={t("reports.detail.companySize")}>
                    {data.company_size_name || none}
                  </Row>
                </dl>
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                  {t("reports.detail.account")}
                </h3>
                <dl>
                  <Row label={t("reports.detail.subscriptionStatus")}>
                    {data.subscription_status
                      ? t(`reports.detail.subStatus.${data.subscription_status}`, {
                          defaultValue: data.subscription_status,
                        })
                      : none}
                  </Row>
                  <Row label={t("reports.detail.couponsUsed")}>
                    {data.coupons_used.length > 0
                      ? data.coupons_used.join(", ")
                      : none}
                  </Row>
                  <Row label={t("reports.detail.accountCreated")}>
                    {formatDate(data.account_created, i18n.language) || none}
                  </Row>
                  <Row label={t("reports.detail.lastLogin")}>
                    {formatDateTime(data.last_login, i18n.language) || none}
                  </Row>
                </dl>
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                  {t("reports.detail.sectors")}
                </h3>
                <p className="text-sm text-foreground">
                  {data.sectors.map((s) => s.name).join(", ") || none}
                </p>
              </section>

              {data.characteristic_names.length > 0 ? (
                <section>
                  <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                    {t("reports.detail.characteristics")}
                  </h3>
                  <p className="text-sm text-foreground">
                    {data.characteristic_names.join(", ")}
                  </p>
                </section>
              ) : null}

              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                  {t("reports.detail.progress")}
                </h3>
                <div className="mb-3">
                  <Pill tone={stepTone(data.current_step)}>
                    {t(`reports.steps.${data.current_step}`)}
                  </Pill>
                </div>
                <ol className="space-y-1">
                  {PROGRESS_STEPS.map((step) => {
                    const reached = data[step];
                    return (
                      <li
                        key={step}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span
                          className={
                            reached
                              ? "text-foreground"
                              : "text-muted-foreground"
                          }
                        >
                          {t(`reports.steps.${step}`)}
                        </span>
                        <span className="text-muted-foreground">
                          {formatDate(reached, i18n.language) || none}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                  {t("reports.detail.consents")}
                </h3>
                <dl>
                  <Row label={t("reports.detail.consentProgress")}>
                    {data.consent_progress_at
                      ? `${t("reports.detail.granted")} · ${formatDateTime(data.consent_progress_at, i18n.language)}`
                      : t("reports.detail.notGranted")}
                  </Row>
                  <Row label={t("reports.detail.consentDiagnostic")}>
                    {data.consent_diagnostic_at
                      ? `${t("reports.detail.granted")} · ${formatDateTime(data.consent_diagnostic_at, i18n.language)}`
                      : t("reports.detail.notGranted")}
                  </Row>
                  <Row label={t("reports.detail.consentEdit")}>
                    {data.consent_edit_at
                      ? `${t("reports.detail.granted")} · ${formatDateTime(data.consent_edit_at, i18n.language)}`
                      : t("reports.detail.notGranted")}
                  </Row>
                </dl>
                {/* Same two destinations, wording and consent gate as the
                    group-detail member rows — MemberDataView decides read-only
                    vs editable from `consent_edit_at` on its own. */}
                {onViewMember && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {data.consent_diagnostic_at ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            onViewMember(data.id, data.display_name, "diagnostic")
                          }
                        >
                          {t("partners.members.linkResults")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onViewMember(data.id, data.display_name, "plan")}
                        >
                          {t("partners.members.linkActionPlan")}
                        </Button>
                      </>
                    ) : (
                      <p className="text-sm italic text-muted-foreground">
                        {t("partners.members.consentNotGranted")}
                      </p>
                    )}
                  </div>
                )}
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                  {t("reports.detail.communication")}
                </h3>
                <dl>
                  <Row label={t("reports.detail.publicationStatus")}>
                    {(() => {
                      const cs = data.communication_status as unknown as CommunicationStatus;
                      const at = cs.at ? formatDateTime(cs.at, i18n.language) : "";
                      if (cs.status === "published")
                        return t("reports.detail.pubPublished", { date: at });
                      if (cs.status === "requested")
                        return t("reports.detail.pubRequested", { date: at });
                      return t("reports.detail.pubNone");
                    })()}
                  </Row>
                  <Row label={t("reports.detail.lastCommunication")}>
                    {formatDateTime(data.last_communication_at, i18n.language) || none}
                  </Row>
                </dl>
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                  {t("reports.detail.actionPlans")}
                </h3>
                {(data.action_plans as unknown as DetailActionPlan[]).length > 0 ? (
                  <ul className="space-y-1 text-sm">
                    {(data.action_plans as unknown as DetailActionPlan[]).map((p) => (
                      <li key={p.id} className="flex justify-between gap-2">
                        <span className="text-foreground">
                          {p.name || t("reports.detail.untitledPlan")}
                        </span>
                        <span className="text-muted-foreground">
                          {formatDate(p.created_at, i18n.language)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">{none}</p>
                )}
              </section>

              {(data.related_profiles as unknown as RelatedProfile[]).length > 0 ? (
                <section>
                  <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                    {t("reports.detail.relatedProfiles")}
                  </h3>
                  <p className="text-sm text-foreground">
                    {(data.related_profiles as unknown as RelatedProfile[])
                      .map((p) => p.name)
                      .join(", ")}
                  </p>
                </section>
              ) : null}

              {data.description ? (
                <section>
                  <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                    {t("reports.detail.description")}
                  </h3>
                  <p className="whitespace-pre-line text-sm text-foreground">
                    {data.description}
                  </p>
                </section>
              ) : null}
            </div>
          ) : null}
        </QueryState>
      </aside>
    </div>
  );
}
